import { useEffect, useRef, useState } from "react";
import { RoleBadge } from "../RoleBadge";
import { SENDER_LIMITS, normalizeProfile, publicKeyToNpub } from "@timecapsule/crypto";
import { finalizeEvent } from "nostr-tools/pure";
import { fingerprint } from "../keys";
import { stretchPassword, passwordProblem } from "@timecapsule/crypto/password";
import { NOTIFICATION_KINDS, api, type ApiToken, type Invite, type InviteCreated, type MailSent, type NotificationKind, type NotificationPrefs, type NotificationsAnswer, type ServerCopy, type SignIn } from "../api";
import { Preferences, link } from "../App";
import { isKeyRequestCancelled, useSession } from "../session";
import { forgetDevice } from "../storage";
import { LOCALES, errorText, useLang, useT } from "../i18n";
import { Loading } from "../Loading";
import { formatDay } from "../time";
import { Disclosure } from "../Disclosure";
import { useRefreshHandler } from "../Refresh";
import { usePagedList } from "../pagedList";
import { ShowMore } from "../ShowMore";
import { PlanSummary } from "./Billing";

/** The editable profile first; preferences and occasional account tools disclose below it. */
export function Account() {
  const t = useT();
  const { me, keyStatus, device, lock } = useSession();
  const [server, setServer] = useState<ServerCopy | null | undefined>(undefined);

  useEffect(() => {
    api.loadServerCopy().then(setServer, () => setServer(undefined));
  }, [me.pubkey]);
  useRefreshHandler(async () => { setServer(await api.loadServerCopy()); });

  // The four actions of PLAN.md 4.11 H8, named the same everywhere.
  async function logout() {
    lock();
    await api.logout();
    location.href = "/";
  }

  async function logoutEverywhere() {
    if (!confirm(t.account.confirmEverywhere)) return;
    lock();
    await api.logoutEverywhere();
    location.href = "/";
  }

  async function forgetThisDevice() {
    if (!confirm(t.account.confirmForget)) return;
    lock();
    await forgetDevice(me.id);
    await api.logout();
    location.href = "/";
  }

  return (
    <div className="account-page">
      <div className="page-head">
        <h1>{me.profile?.display_name ?? me.display_name}</h1>
        {(me.role !== "member" || me.kind === "service") && <p className="role-line"><RoleBadge role={me.role} kind={me.kind} /></p>}
        <p className="muted small">{me.email} · {t.design.accountNote}</p>
      </div>

      <div className="account-sections">
        {/* The first ten seconds (reviews/06-action-plan.md 3.11): the key's state, then language and theme. */}
        <Disclosure title={t.account.yourKey} open>
          <p className="small"><a href="/keys" onClick={link("/keys")}>{t.account.manageKeys}</a></p>
          {me.pubkey && <p className="small"><a href="/try" onClick={link("/try")}>{t.tryIt.accountLink}</a>{t.tryIt.accountNote}</p>}
          {me.pubkey ? (
            <>
              <p className="small">
                {t.account.identity}
                <code className="select">{publicKeyToNpub(me.pubkey)}</code>
              </p>
              <p className="small">
                {t.account.status[keyStatus]}
                {keyStatus === "unlocked" && device?.kind === "plain" && t.account.plain}{" "}
                {keyStatus === "unlocked" && device?.kind === "prf-pin" && (
                  <button className="link small" onClick={lock}>
                    {t.account.lock}
                  </button>
                )}
              </p>
              <p className="small">
                {server === undefined ? "" : server === null ? t.account.noServerCopy : server.locked ? t.account.serverBlocked : t.account.serverCopy(server.attempts_left)}
              </p>
              <p className="small">
                <a href="/restore" onClick={link("/restore")}>
                  {t.account.setUpAgain}
                </a>
                {t.account.setUpAgainNote}
              </p>
            </>
          ) : (
            <p className="small">{t.account.notSetUp}</p>
          )}
        </Disclosure>

        <Disclosure title={t.account.preferences} open><Preferences /></Disclosure>

        {me.pubkey && <ProfileForm />}

        {me.kind !== "service" && <PlanSummary />}

        <Disclosure title={t.account.privacy}><PrivacySettings /></Disclosure>

        {me.kind !== "service" && <Disclosure title={t.account.notifications.title}><NotificationSettings /></Disclosure>}

        {me.kind === "service" && <Disclosure title={t.developers.title}><DeveloperTokens /></Disclosure>}


        <SignInMethods />

        <Disclosure title={t.account.following}>
          <p className="small">
            <a href="/following" onClick={link("/following")}>
              {t.account.peopleLink}
            </a>
            {t.account.peopleNote}
          </p>
          <p className="small">
            <a href="/contacts" onClick={link("/contacts")}>
              {t.account.contactsLink}
            </a>
            {t.account.contactsNote}
          </p>
        </Disclosure>

        <InviteSomeone />

        <Disclosure title={t.account.deviceTitle}>
          <ul className="plain small">{t.account.actions}</ul>
          <div className="chips">
            {keyStatus === "unlocked" && device?.kind === "prf-pin" && <button onClick={lock}>{t.account.lock}</button>}
            <button onClick={logout}>{t.account.logout}</button>
            <button onClick={logoutEverywhere}>{t.account.logoutEverywhere}</button>
            <button onClick={forgetThisDevice}>{t.account.forget}</button>
          </div>
          <h3>{t.account.keepTitle}</h3>
          <p className="small">
            <a href="/vrelis-opener.html" download="vrelis-opener.html">
              {t.account.opener}
            </a>
            {t.account.openerNote}
          </p>
        </Disclosure>
      </div>
    </div>
  );
}

/**
 * Profile of the active identity (PLAN.md 4.10): what new capsules carry as the sender snapshot.
 * Capsules already sent keep the name they were sent with. The change is a kind 0 event signed
 * with the key, so it needs the key unlocked here.
 */
/**
 * API tokens of a service account (PLAN.md 4.16 K5): made after "Confirm it is you", shown once,
 * revoked with the login alone. A token reads and sends capsules; it cannot manage keys or tokens.
 */
function DeveloperTokens() {
  const t = useT();
  const { confirmIdentity } = useSession();
  const [list, setList] = useState<ApiToken[] | null>(null);
  const [name, setName] = useState("");
  const [readOnly, setReadOnly] = useState(false);
  const [shown, setShown] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const load = () => api.tokens().then(setList, (err) => setError(errorText(err)));
  useEffect(() => { void load(); }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await confirmIdentity(t.reauth.purposes.createToken);
      const made = await api.createToken(name.trim(), readOnly ? ["capsules:read"] : ["capsules:read", "capsules:write"]);
      setShown(made.token);
      setName("");
      await load();
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card">
      <h2>{t.developers.title}</h2>
      <p className="small muted">{t.developers.note} <a href="/developers" onClick={link("/developers")}>{t.developers.docsLink}</a></p>
      {shown && (
        <div className="warn small">
          <p>{t.developers.shownOnce}</p>
          <code className="select">{shown}</code>
          <button type="button" className="small" onClick={() => setShown(null)}>{t.developers.copied}</button>
        </div>
      )}
      <form onSubmit={create}>
        <label>{t.developers.tokenName}<input value={name} maxLength={60} onChange={(e) => setName(e.target.value)} disabled={busy} /></label>
        <label className="radio small"><input type="checkbox" checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} disabled={busy} /> {t.developers.readOnly}</label>
        <button className="primary" type="submit" disabled={busy || !name.trim()}>{t.developers.create}</button>
      </form>
      <h3>{t.developers.tokensTitle}</h3>
      {list && list.length === 0 && <p className="small muted">{t.developers.noTokens}</p>}
      <ul className="rows small">
        {list?.map((tk) => (
          <li key={tk.id}>
            <span className="grow">{tk.name} <code>{tk.prefix}…</code> <span className="muted">{tk.scopes.includes("capsules:write") ? t.developers.readWrite : t.developers.readOnlyShort} · {tk.revoked_at ? t.developers.revoked : tk.last_used_at ? t.developers.lastUsed(formatDay(tk.last_used_at * 1000)) : t.developers.neverUsed}</span></span>
            {!tk.revoked_at && <button className="link small" onClick={() => void api.revokeToken(tk.id).then(load, (err) => setError(errorText(err)))}>{t.developers.revoke}</button>}
          </li>
        ))}
      </ul>
      {error && <p className="error small">{error}</p>}
    </section>
  );
}

/** The member's own list setting (PLAN.md 4.16 K10), kept on the server so it holds on every device. */
function PrivacySettings() {
  const t = useT();
  const { me, refresh } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function toggle(hide: boolean) {
    setBusy(true);
    setError(null);
    try {
      await api.saveSettings({ hide_senders: hide });
      await refresh();
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="card">
      <h2>{t.account.privacy}</h2>
      <label className="radio">
        <input type="checkbox" checked={me.settings.hide_senders} onChange={(e) => void toggle(e.target.checked)} disabled={busy} /> {t.account.hideSenders}
      </label>
      <p className="small muted">{t.account.hideSendersNote}</p>
      {error && <p className="error small">{error}</p>}
    </section>
  );
}

/** Capsule notifications by mail (PLAN.md 4.28): one switch per kind, how far ahead, the attachment. */
function NotificationSettings() {
  const t = useT();
  const { lang } = useLang();
  const [answer, setAnswer] = useState<NotificationsAnswer | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api.notifications().then(setAnswer, (err: unknown) => setError(errorText(err)));
  }, []);
  useRefreshHandler(async () => { setAnswer(await api.notifications()); });

  async function save(kind: NotificationKind, change: Partial<NotificationPrefs>) {
    setBusy(true);
    setError(null);
    try {
      setAnswer(await api.saveNotifications({ [kind]: change }, lang));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }
  const hours = (n: number) => new Intl.NumberFormat(LOCALES[lang].locale, { style: "unit", unit: "hour", unitDisplay: "long" }).format(n);
  const n = t.account.notifications;
  return (
    <section className="card">
      <h2>{n.title}</h2>
      <p className="small muted">{n.note}</p>
      {answer === null && !error && <Loading className="muted" />}
      {answer &&
        NOTIFICATION_KINDS.map((kind) => {
          const prefs = answer.notifications[kind];
          return (
            <div key={kind} className="notify-kind">
              <label className="radio">
                <input type="checkbox" checked={prefs.enabled} onChange={(e) => void save(kind, { enabled: e.target.checked })} disabled={busy} /> {n.kinds[kind]}
              </label>
              {prefs.lead_hours !== undefined && (
                <label className="small notify-option">
                  {n.lead}{" "}
                  <select value={prefs.lead_hours} onChange={(e) => void save(kind, { lead_hours: Number(e.target.value) })} disabled={busy || !prefs.enabled}>
                    {answer.lead_hours.map((h) => <option key={h} value={h}>{hours(h)}</option>)}
                  </select>
                </label>
              )}
              {prefs.attach !== undefined && (
                <>
                  <label className="radio small notify-option">
                    <input type="checkbox" checked={prefs.attach} onChange={(e) => void save(kind, { attach: e.target.checked })} disabled={busy || !prefs.enabled} /> {n.attach}
                  </label>
                  <p className="small muted notify-option">{n.attachNote}</p>
                </>
              )}
            </div>
          );
        })}
      <p className="small muted">{n.utc}</p>
      {error && <p className="error small">{error}</p>}
    </section>
  );
}

function ProfileForm() {
  const t = useT();
  const { me, refresh, withKey } = useSession();
  const [displayName, setDisplayName] = useState(me.profile?.display_name ?? "");
  const [name, setName] = useState(me.profile?.name ?? "");
  const [about, setAbout] = useState(me.profile?.about ?? "");
  const baseline = useRef({ displayName, name, about });
  useEffect(() => {
    const next = { displayName: me.profile?.display_name ?? "", name: me.profile?.name ?? "", about: me.profile?.about ?? "" };
    const previous = baseline.current;
    if (displayName === previous.displayName && name === previous.name && about === previous.about) {
      setDisplayName(next.displayName); setName(next.name); setAbout(next.about);
    }
    baseline.current = next;
  }, [me.profile]);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setStatus(null);
    try {
      const profile = normalizeProfile({ display_name: displayName, name, about });
      const event = await withKey(t.keyDialog.purposes.profile, async (key) =>
        finalizeEvent({ kind: 0, created_at: Math.floor(Date.now() / 1000), tags: [], content: JSON.stringify(profile) }, key),
      );
      await api.saveProfile(event);
      await refresh();
      setStatus(t.account.saved);
    } catch (err) {
      if (!isKeyRequestCancelled(err)) setError(errorText(err));
    }
  }

  return (
    <form className="card" onSubmit={save}>
      <h2>{t.account.profileTitle}</h2>
      <details className="small muted profile-note"><summary>{t.design.details}</summary><p>{t.account.profileNote(fingerprint(me.pubkey!))}</p></details>
      <div className="fields">
        <label>
          {t.account.displayName}
          <input value={displayName} maxLength={64} onChange={(e) => setDisplayName(e.target.value)} />
        </label>
        <label>
          {t.account.nickname}
          <input value={name} maxLength={32} onChange={(e) => setName(e.target.value)} />
        </label>
      </div>
      <label>
        {t.account.about(SENDER_LIMITS.about)}
        <textarea rows={2} value={about} onChange={(e) => setAbout(e.target.value)} />
      </label>
      <button type="submit" className="primary">{t.account.saveProfile}</button>
      {status && <p className="note small">{status}</p>}
      {error && <p className="error small">{error}</p>}
    </form>
  );
}

/**
 * The member's sign-in methods (PLAN.md 4.14): Google, a password, passkeys. Each one opens the same
 * account; none of them opens the key. A password is added through a mail link to the account's
 * address; a passkey is added when it is created to protect the key.
 */
function SignInMethods() {
  const t = useT();
  const { lang } = useLang();
  const { me } = useSession();
  const [list, setList] = useState<SignIn[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState<MailSent | null>(null);

  const load = () => api.signIns().then(setList, (err: unknown) => setError(errorText(err)));
  useRefreshHandler(load);
  useEffect(() => {
    void load();
  }, []);

  async function remove(s: SignIn) {
    if (!confirm(t.account.signIns.confirmRemove(t.account.signIns.kind[s.provider]))) return;
    setError(null);
    try {
      await api.removeSignIn(s.id);
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  }

  async function addPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      setSent(await api.addPassword({ stretched: await stretchPassword(password, me.email), lang }));
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  const problem = password.length === 0 ? null : passwordProblem(password);
  const hasPassword = list?.some((s) => s.provider === "password") ?? true;

  return (
    <Disclosure title={t.account.signIns.title}>
      <p className="small muted">{t.account.signIns.note}</p>
      {list && (
        <ul className="rows small">
          {list.map((s) => (
            <li key={s.id}>
              <span className="grow">
                {t.account.signIns.kind[s.provider]}
                {s.label ? `: ${s.label}` : ""} <span className="muted">{t.account.signIns.since(formatDay(s.created_at * 1000))}</span>
              </span>
              {list.length > 1 && (
                <button className="link small" onClick={() => void remove(s)}>
                  {t.account.signIns.remove}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {list && !list.some((s) => s.provider === "passkey") && <p className="small muted">{t.account.signIns.noPasskey}</p>}
      {!hasPassword &&
        (sent ? (
          <p className="small">
            {t.login.password.checkMail(me.email)}
            {sent.dev_link && (
              <>
                {" "}
                Dev: <a href={sent.dev_link}>{sent.dev_link}</a>
              </>
            )}
          </p>
        ) : (
          <form onSubmit={addPassword}>
            <p className="small">{t.account.signIns.addPassword(me.email)}</p>
            <div className="fields">
              <label>
                {t.login.password.newPassword}
                <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </label>
              <label>
                {t.login.password.repeat}
                <input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} />
              </label>
              {problem && <p className="error small">{t.login.password.rule}</p>}
              {repeat.length > 0 && password !== repeat && <p className="error small">{t.login.password.differ}</p>}
            </div>
            <button type="submit" disabled={busy || !!problem || password.length === 0 || password !== repeat}>
              {busy ? t.login.password.busy : t.account.signIns.addPasswordButton}
            </button>
          </form>
        ))}
      {error && <p className="error small">{error}</p>}
    </Disclosure>
  );
}

/**
 * Any member invites by address (PLAN.md 4.14): the server mails the link; the inviter and the new
 * member then follow each other. At most 10 open invites.
 */
function InviteSomeone() {
  const t = useT();
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<InviteCreated | null>(null);
  const [error, setError] = useState<string | null>(null);
  const now = Math.floor(Date.now() / 1000);
  // My invites, 20 at a time, newest first (PLAN.md 4.20).
  const { me } = useSession();
  const invitePages = usePagedList(`invites-mine:${me.id}`, (cursor) => api.invites(true, cursor));
  const mine = invitePages.items;
  const load = invitePages.reload;

  async function send(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      setResult(await api.createInvite({ email: email.trim(), expires_in_days: 14, lang }));
      setEmail("");
      await load();
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  async function revoke(i: Invite) {
    setError(null);
    try {
      await api.revokeInvite(i.code);
      await load();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Disclosure title={t.invites.title}>
      <p className="small">{t.invites.text} {me.limits.open_invites!=null&&t.invites.limit(me.limits.open_invites)}</p>
      <form className="form-row" onSubmit={send}>
        <label>
          {t.login.password.email}
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </label>
        <button type="submit" disabled={busy || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())}>
          {t.invites.send}
        </button>
      </form>
      {result && (
        <p className="note small">
          {result.mailed ? (result.resent ? t.invites.resent : t.invites.mailed)(result.invite.email ?? "") : t.invites.notMailed(errorText({ code: result.mail_error }))} {t.invites.orCopy}{" "}
          <code className="select">{result.link}</code>{" "}
          <button type="button" className="link" onClick={() => navigator.clipboard?.writeText(result.link)}>
            {t.common.copy}
          </button>
        </p>
      )}
      {mine && mine.length > 0 && (
        <ul className="rows small">
          {mine.map((i) => (
            <li key={i.code}>
              <span className="grow">{i.email ?? i.code}</span>
              <span className="muted">{i.revoked_at ? t.pilotAdmin.revoked : i.used_by !== null ? t.invites.used : i.expires_at < now ? t.admin.expired : t.invites.open}</span>
              {i.used_by === null && !i.revoked_at && i.expires_at >= now && (
                <button className="link small" onClick={() => void revoke(i)}>
                  {t.admin.revoke}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      <ShowMore hasMore={invitePages.hasMore} busy={invitePages.loadingMore} onMore={() => void invitePages.loadMore()} />
      {(error || invitePages.error) && <p className="error small">{error || invitePages.error}</p>}
    </Disclosure>
  );
}
